import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminService } from '../../services/adminService';
import {
  FiSearch,
  FiFilter,
  FiDownload,
  FiCalendar,
  FiRefreshCw
} from 'react-icons/fi';

const SubscriptionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterPackage, setFilterPackage] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('expiryDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterStatus, filterPackage, searchTerm, dateFrom, dateTo, sortBy, sortOrder]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        limit: 50,
        sortBy,
        sortOrder
      };

      if (filterStatus.length > 0) {
        params.status = filterStatus.join(',');
      }

      if (filterPackage.length > 0) {
        params.package = filterPackage.join(',');
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (dateFrom) {
        params.dateFrom = dateFrom;
      }

      if (dateTo) {
        params.dateTo = dateTo;
      }

      const response = await adminService.getAllSubscriptions(params);
      const apiSubscriptions = response.data.subscriptions.map(sub => ({
        id: sub.id,
        businessName: sub.businessName,
        username: sub.username,
        email: sub.email,
        package: sub.package || 'trial',
        status: sub.status,
        startDate: sub.startDate ? new Date(sub.startDate).toLocaleDateString('en-GB') : 'N/A',
        expiryDate: sub.expiryDate ? new Date(sub.expiryDate).toLocaleDateString('en-GB') : 'N/A',
        amount: sub.amount,
        isFreeTrialUser: sub.isFreeTrialUser
      }));

      setSubscriptions(apiSubscriptions);
      setTotalCount(response.data.pagination.total);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus([]);
    setFilterPackage([]);
    setDateFrom('');
    setDateTo('');
    setSortBy('expiryDate');
    setSortOrder('asc');
    setCurrentPage(1);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterStatus.length > 0) count++;
    if (filterPackage.length > 0) count++;
    if (dateFrom || dateTo) count++;
    return count;
  };

  const toggleStatusFilter = (status) => {
    setFilterStatus(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    setCurrentPage(1);
  };

  const togglePackageFilter = (pkg) => {
    setFilterPackage(prev =>
      prev.includes(pkg)
        ? prev.filter(p => p !== pkg)
        : [...prev, pkg]
    );
    setCurrentPage(1);
  };

  const handleExport = async (format) => {
    try {
      // Build filters from current state
      const filters = {};

      if (filterStatus.length > 0) {
        filters.status = filterStatus.join(',');
      }

      if (filterPackage.length > 0) {
        filters.package = filterPackage.join(',');
      }

      if (searchTerm) {
        filters.search = searchTerm;
      }

      if (dateFrom) {
        filters.dateFrom = dateFrom;
      }

      if (dateTo) {
        filters.dateTo = dateTo;
      }

      filters.sortBy = sortBy;
      filters.sortOrder = sortOrder;

      // Get the export URL from service
      let result;
      if (format === 'excel') {
        result = await adminService.exportSubscriptionsExcel(filters);
      } else if (format === 'pdf') {
        result = await adminService.exportSubscriptionsPDF(filters);
      }

      // Get auth token from localStorage
      const token = localStorage.getItem('token');

      // Create full URL with API base
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
      const fullUrl = `${apiUrl}${result.url}`;

      // Fetch with auth header and download
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `subscriptions_${Date.now()}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        console.error('Export failed:', response.statusText);
        alert('Export failed. Please try again.');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Export failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Subscriptions">
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  const getPackageBadge = (pkg) => {
    const badges = {
      trial: 'bg-blue-100 text-blue-800',
      base: 'bg-green-100 text-green-800',
      basic_plus: 'bg-indigo-100 text-indigo-800'
    };

    const labels = {
      trial: 'Trial',
      base: 'Base',
      basic_plus: 'Pro'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badges[pkg]}`}>
        {labels[pkg]}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <AdminLayout title="Subscriptions">
      <div className="space-y-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-6">
          {/* Main Filter Row */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
            {/* Search */}
            <div className="md:col-span-5">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by business name or email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Sort By */}
            <div className="md:col-span-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="expiryDate">Expiry Date</option>
                <option value="startDate">Start Date</option>
                <option value="businessName">Business Name</option>
                <option value="amount">Amount</option>
              </select>
            </div>

            {/* Sort Order */}
            <div className="md:col-span-1">
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="desc">↓</option>
                <option value="asc">↑</option>
              </select>
            </div>

            {/* Export Buttons */}
            <div className="md:col-span-3 flex space-x-2">
              <button
                onClick={() => handleExport('excel')}
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center space-x-2"
                title="Export to Excel"
              >
                <FiDownload className="w-4 h-4" />
                <span>Excel</span>
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center justify-center space-x-2"
                title="Export to PDF"
              >
                <FiDownload className="w-4 h-4" />
                <span>PDF</span>
              </button>
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <div className="flex items-center justify-between border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-700 font-medium"
            >
              <FiFilter className="w-4 h-4" />
              <span>Advanced Filters</span>
              {getActiveFiltersCount() > 0 && (
                <span className="px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
            {getActiveFiltersCount() > 0 && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-700 text-sm"
              >
                <FiRefreshCw className="w-4 h-4" />
                <span>Clear All Filters</span>
              </button>
            )}
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Status Filter - Multi-select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="space-y-2">
                  {['active', 'inactive', 'expired'].map((status) => (
                    <label key={status} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterStatus.includes(status)}
                        onChange={() => toggleStatusFilter(status)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Package Filter - Multi-select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Package</label>
                <div className="space-y-2">
                  {[
                    { value: 'trial', label: 'Trial' },
                    { value: 'base', label: 'Base' },
                    { value: 'basic_plus', label: 'Pro' }
                  ].map((pkg) => (
                    <label key={pkg.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterPackage.includes(pkg.value)}
                        onChange={() => togglePackageFilter(pkg.value)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">{pkg.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Date Range Filter - Expiry Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiCalendar className="inline w-4 h-4 mr-1" />
                  Expiry Date Range
                </label>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Subscriptions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Business Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Package
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Expiry Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{sub.businessName}</div>
                        <div className="text-sm text-gray-500">@{sub.username}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{sub.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPackageBadge(sub.package)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(sub.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sub.startDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sub.expiryDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {sub.isFreeTrialUser ? (
                        <span className="text-blue-600">Free Trial</span>
                      ) : (
                        `BHD ${sub.amount.toFixed(3)}`
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing page {currentPage} of {totalPages} ({totalCount} total subscriptions)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SubscriptionsPage;
